import { X } from 'lucide-react'

import Button from '@/components/Button/Button'

import SmartTimePicker from '@/authenticated/components/SmartTimePicker'
import { InactiveTime } from './useInactiveTimesViewModel'
import useInactiveTimeRowViewModel from './useInactiveTimeRowViewModel'

interface InactiveTimeRowProps {
  value: InactiveTime
  onRemove: (id: string) => void
  onChange: (
    id: string,
    patch: Partial<Pick<InactiveTime, 'startTime' | 'endTime' | 'days'>>,
  ) => void
  onToggleDay: (id: string, dayIndex: number) => void
}

export default function InactiveTimeRow(props: InactiveTimeRowProps) {
  const viewModel = useInactiveTimeRowViewModel(props)
  const {
    id,
    startTime,
    endTime,
    days,
    daysOfWeek,
    crossesOverMidnight,
    daysDisplayText,
    endsText,
    handleRemove,
    handleStartTimeChange,
    handleEndTimeChange,
    handleToggleDay,
  } = viewModel

  return (
    <div className="relative justify-center p-4 bg-gray-200 rounded-2.5xl">
      <div className="absolute -top-2 -right-2">
        <Button
          id={`remove-${id}`}
          onClick={handleRemove}
          variant="accent-subtle"
          className="w-6 h-6 rounded-full"
          small
          aria-label="Remove time range"
        >
          <X size={17} className="mt-[3px]" />
        </Button>
      </div>
      <div className="flex items-center justify-center gap-2 mb-4">
        <div className="text-sm font-bold text-center text-gray-700">
          Time range:
        </div>
        <SmartTimePicker
          onChange={handleStartTimeChange}
          hour={Number(startTime.split(':')[0])}
          minute={Number(startTime.split(':')[1])}
        />
        <span className="text-sm font-bold text-gray-700">-</span>
        <SmartTimePicker
          onChange={handleEndTimeChange}
          hour={Number(endTime.split(':')[0])}
          minute={Number(endTime.split(':')[1])}
          showPlusOne={crossesOverMidnight}
        />
      </div>
      <div className="flex justify-center gap-2">
        {daysOfWeek.map((day, index) => (
          <Button
            id={`day-${index}`}
            key={day.full}
            variant={days.includes(index) ? 'accent' : 'secondary'}
            onClick={() => handleToggleDay(index)}
            className="w-8 h-8 p-0 rounded-lg"
          >
            {day.short.charAt(0)}
          </Button>
        ))}
      </div>
      <div className="flex items-center justify-center h-5 mt-4 text-center">
        <span className="text-sm text-gray-600">{daysDisplayText}</span>
      </div>
      <div className="flex items-center justify-center mt-1 text-center">
        <span className="text-sm text-gray-600">{endsText}</span>
      </div>
    </div>
  )
}
